/** The app's sync engine instance and React bindings. */
import { isReachable, subscribeToReachability } from '@/lib/api/client';
import { ensureOnline, useSession } from '@/lib/auth/session';
import { pausedProjects, withoutPaused } from '@/lib/db/device';
import { listProjects } from '@/lib/db/projects';
import { deviceInfo } from '@/lib/device-info';
import { getDeviceId } from '@/lib/id';
import { processMediaQueue } from '@/lib/media/uploader';

import { syncApi } from './api';
import { SyncEngine, useSyncSnapshot, type SyncSnapshot } from './engine';
import { deriveIndicator, type SyncIndicator } from './status';

export const syncEngine = new SyncEngine({
  api: syncApi,
  ensureOnline,
  // Pull only projects the server knows about; locally created ones are pulled
  // once their create mutation has been applied. Projects switched off for
  // offline use in the Sync Center are not pulled (module 13).
  projectIds: async () =>
    withoutPaused(
      (await listProjects())
        .filter((project) => project.membership !== null || project.version !== null)
        .map((project) => project.id),
      await pausedProjects(),
    ),
  deviceId: getDeviceId,
  deviceInfo,
  processMedia: () => processMediaQueue(),
});

subscribeToReachability(() => {
  syncEngine.setConnectivity(isReachable() ? 'online' : 'offline');
});

export function useSyncStatus(): SyncSnapshot {
  return useSyncSnapshot(syncEngine);
}

export function useSyncIndicator(): SyncIndicator {
  const sync = useSyncStatus();
  const session = useSession();
  return deriveIndicator(sync, session.status);
}

export function syncNow(): Promise<unknown> {
  return syncEngine.run();
}
